/**
 * ч.1197 (рішення власника): зміна клієнта З ПОПЕРЕДЖЕННЯМ.
 *
 * Перемикання клієнта закриває всі відкриті вкладки і стирає незбережені
 * правки (чернетки) клієнта, якого користувач покидає, — тому спершу питаємо.
 * Це ЄДИНИЙ спосіб перемкнути клієнта з інтерфейсу (фільтр клієнта у шапці,
 * «перейти» у «Моїх доступах»): напряму `switchTenant` з компонентів не кличемо.
 * Якщо закривати нічого (вкладок і незбережених правок немає) — без питання.
 */
import { useCallback } from 'react';
import { useConfirm } from '@/components/ConfirmProvider';
import { switchTenant } from '@/auth/switchTenant';
import { unsavedPaths } from '@/store/formDrafts';
import { useOpenTabs } from '@/store/openTabs';

/** Текст попередження: скільки вкладок закриється і чи є незбережене. */
export function switchTenantWarning(tabs: number, unsaved: number, tenantName?: string): string {
  const lines = [
    tenantName ? `Перейти до клієнта «${tenantName}»?` : 'Перейти до іншого клієнта?',
    '',
    'Усі відкриті вкладки буде закрито, а незбережені дані на них — втрачено.',
    `Відкрито вкладок: ${tabs}.`,
  ];
  if (unsaved > 0) {
    lines.push(`З незбереженими змінами: ${unsaved}. Щоб не втратити їх, `
      + 'натисніть «скасувати» і збережіть документи.');
  }
  return lines.join('\n');
}

/** Повертає функцію «перемкнути клієнта»: true — перемкнули, false — користувач передумав. */
export function useSwitchTenant(): (tenantId: string, tenantName?: string) => Promise<boolean> {
  const confirm = useConfirm();
  return useCallback(async (tenantId: string, tenantName?: string) => {
    const tabs = useOpenTabs.getState().tabs.length;
    const unsaved = unsavedPaths().length;
    if (tabs > 0 || unsaved > 0) {
      const ok = await confirm({
        title: 'Зміна клієнта',
        message: switchTenantWarning(tabs, unsaved, tenantName),
        okLabel: 'перейти',
        cancelLabel: 'скасувати',
        danger: unsaved > 0,
      });
      if (!ok) return false;
    }
    switchTenant(tenantId);
    return true;
  }, [confirm]);
}
